import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import FunzoneStyledQr from '../../../components/FunzoneStyledQr';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import AuthContext from '../../../../context/authContext';

function OnsiteQrModal({ open, onClose, booking, onBookingUpdated }) {
  const authCtx = useContext(AuthContext);
  const { setOnsiteAccessMode } = useInternalBooking();
  const token = booking?.publicAccessToken || null;
  const storedPin = booking?.onsiteAccessPin || null;
  const defaultMode = authCtx?.preferences?.onsiteQrDefaultMode || 'token';

  const [mode, setMode] = useState(defaultMode);
  const [pin, setPin] = useState(storedPin);
  const [isGeneratingPin, setIsGeneratingPin] = useState(false);
  const pinRequestInFlightRef = useRef(false);
  const wasOpenRef = useRef(false);
  const lastBookingIdRef = useRef(booking?._id || null);

  const ensurePinExists = async ({ regeneratePin = false } = {}) => {
    if (!booking?._id) return;
    if (pinRequestInFlightRef.current) return;
    pinRequestInFlightRef.current = true;
    setIsGeneratingPin(true);
    try {
      const resp = await setOnsiteAccessMode({
        bookingId: booking._id,
        mode: 'pin',
        regeneratePin,
      });
      const nextPin = resp?.data?.onsiteAccessPin || null;
      setPin(nextPin);

      if (typeof onBookingUpdated === 'function' && booking) {
        onBookingUpdated({
          ...booking,
          onsiteAccessPin: nextPin,
          onsiteAccessPinCreatedAt:
            resp?.data?.onsiteAccessPinCreatedAt || booking?.onsiteAccessPinCreatedAt,
        });
      }
    } catch (e) {
      toast.error('Fehler beim Generieren der PIN');
      throw e;
    } finally {
      pinRequestInFlightRef.current = false;
      setIsGeneratingPin(false);
    }
  };

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    // Always start with the user's preference when opening the modal (do not persist mode per booking).
    if (!wasOpenRef.current || (booking?._id || null) !== lastBookingIdRef.current) {
      setMode(defaultMode);
      wasOpenRef.current = true;
      lastBookingIdRef.current = booking?._id || null;
    }

    setPin(storedPin);

    if (defaultMode === 'pin' && !storedPin) {
      void ensurePinExists({ regeneratePin: false });
    }
  }, [open, booking?._id, defaultMode, storedPin]);

  const customerBase =
    (import.meta && import.meta.env && import.meta.env.VITE_CUSTOMER_FRONTEND_BASE_URL) || '';
  const effectiveBase =
    customerBase || (typeof window !== 'undefined' ? window.location.origin : '');

  const link = useMemo(() => {
    if (mode === 'pin') return `${effectiveBase}/onsite`;
    return token ? `${effectiveBase}/onsite/${token}` : '';
  }, [mode, token, effectiveBase]);

  const handleModeChange = nextMode => {
    if (!nextMode) return;
    setMode(nextMode);

    if (nextMode === 'pin' && !pin) {
      void ensurePinExists({ regeneratePin: false });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='onsite-qr-modal-title'
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 560,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography id='onsite-qr-modal-title' variant='h6' sx={{ fontWeight: 700 }}>
              Onsite QR-Code
            </Typography>
            <IconButton onClick={onClose} size='small'>
              <CloseIcon />
            </IconButton>
          </Stack>

          <ToggleButtonGroup
            value={mode}
            exclusive
            fullWidth
            onChange={async (_, next) => {
              if (!next) return;
              if (next === mode) return;
              handleModeChange(next);
            }}
          >
            <ToggleButton value='token'>Token</ToggleButton>
            <ToggleButton value='pin'>PIN</ToggleButton>
          </ToggleButtonGroup>

          {mode === 'pin' && (
            <Stack spacing={1} alignItems='center'>
              <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
                Kunde öffnet den Link und gibt die PIN ein.
              </Typography>
              <Typography
                variant='h4'
                sx={{ fontWeight: 800, letterSpacing: '0.2em', lineHeight: 1 }}
              >
                {pin || '----'}
              </Typography>
              <Button
                variant='outlined'
                onClick={async () => {
                  await ensurePinExists({ regeneratePin: true });
                }}
                disabled={isGeneratingPin}
              >
                {isGeneratingPin ? <CircularProgress size={18} /> : 'Neue PIN generieren'}
              </Button>
            </Stack>
          )}

          {link ? (
            <Stack spacing={2} alignItems='center'>
              <FunzoneStyledQr data={link} size={360} maxSize={420} margin={16} readyDelayMs={0} />
              <Typography variant='caption' color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                {link}
              </Typography>
            </Stack>
          ) : (
            <Typography variant='body2'>Kein Token für diese Buchung vorhanden.</Typography>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}

export default OnsiteQrModal;
